<template>
  <div class="user-management">
    <!-- 圆形退出按钮 -->
    <div class="logout-button-container">
      <button class="logout-button" @click="handleLogout" title="退出登录">
        <i class="el-icon-switch-button"></i>
      </button>
    </div>

    <!-- 页面标题 -->
    <div class="page-header">
      <div class="left-section">
        <el-button icon="el-icon-back" @click="goBack">返回</el-button>
        <h2>用户管理</h2>
      </div>
      <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新增用户</el-button>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索用户名/昵称"
        clearable
        style="width: 300px"
        @keyup.enter.native="handleSearch"
      >
        <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
      </el-input>
    </div>

    <!-- 用户列表 -->
    <div class="table-container">
      <el-table :data="userList" border stripe>
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="username" label="用户名" width="150"></el-table-column>
        <el-table-column prop="nickname" label="昵称" width="150"></el-table-column>
        <el-table-column prop="email" label="邮箱"></el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180"></el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="small" style="color: #f56c6c" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        :total="total"
        layout="total, prev, pager, next"
        @current-change="handlePageChange"
      ></el-pagination>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form :model="userForm" :rules="isEdit ? editRules : rules" ref="userForm" label-width="80px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="userForm.username" :disabled="isEdit"></el-input>
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input v-model="userForm.password" type="password" :placeholder="isEdit ? '留空则不修改' : '请输入密码'"></el-input>
        </el-form-item>
        <el-form-item label="昵称" prop="nickname">
          <el-input v-model="userForm.nickname"></el-input>
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="userForm.email"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getUserList, addUser, updateUser, deleteUser } from '@/api/user'
import { logout as logoutApi } from '@/api/auth'
import { removeToken } from '@/utils/auth'

export default {
  name: 'UserManagement',
  data() {
    return {
      userList: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      searchKeyword: '',
      dialogVisible: false,
      isEdit: false,
      dialogTitle: '',
      userForm: {
        id: null,
        username: '',
        password: '',
        nickname: '',
        email: ''
      },
      rules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于 6 个字符', trigger: 'blur' }
        ],
        nickname: [
          { required: true, message: '请输入昵称', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ]
      },
      editRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        password: [
          { min: 6, message: '密码长度不能少于 6 个字符', trigger: 'blur' }
        ],
        nickname: [
          { required: true, message: '请输入昵称', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.fetchUserList()
  },
  methods: {
    // 返回上一页
    goBack() {
      this.$router.push('/')
    },

    // 退出登录
    handleLogout() {
      this.$confirm('确定要退出登录吗?', '确认退出', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        logoutApi().then(() => {
          removeToken()
          this.$router.push('/login')
          this.$message.success('退出登录成功')
        }).catch(() => {
          removeToken()
          this.$router.push('/login')
          this.$message.success('退出登录成功')
        })
      }).catch(() => {})
    },

    // 获取用户列表
    fetchUserList() {
      getUserList({
        page: this.currentPage,
        size: this.pageSize,
        keyword: this.searchKeyword
      }).then(res => {
        const pageData = res.data
        this.userList = pageData.records || []
        this.total = pageData.total || 0
      }).catch(err => {
        console.error('获取用户列表失败:', err)
        this.$message.error('获取用户列表失败')
      })
    },

    // 搜索
    handleSearch() {
      this.currentPage = 1
      this.fetchUserList()
    },

    // 分页
    handlePageChange(page) {
      this.currentPage = page
      this.fetchUserList()
    },

    // 新增
    handleAdd() {
      this.isEdit = false
      this.dialogTitle = '新增用户'
      this.userForm = {
        id: null,
        username: '',
        password: '',
        nickname: '',
        email: ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.userForm && this.$refs.userForm.clearValidate()
      })
    },

    // 编辑
    handleEdit(row) {
      this.isEdit = true
      this.dialogTitle = '编辑用户'
      this.userForm = {
        id: row.id,
        username: row.username,
        password: '',
        nickname: row.nickname,
        email: row.email
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.userForm && this.$refs.userForm.clearValidate()
      })
    },

    // 删除
    handleDelete(row) {
      this.$confirm(`确定要删除用户"${row.username}"吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteUser(row.id).then(res => {
          this.$message.success(res.message || '删除成功')
          this.fetchUserList()
        }).catch(err => {
          this.$message.error(err.message || '删除失败')
        })
      }).catch(() => {})
    },

    // 提交
    handleSubmit() {
      this.$refs.userForm.validate(valid => {
        if (valid) {
          if (this.isEdit) {
            // 编辑
            updateUser(this.userForm).then(res => {
              this.$message.success(res.message || '更新成功')
              this.dialogVisible = false
              this.fetchUserList()
            }).catch(err => {
              this.$message.error(err.message || '更新失败')
            })
          } else {
            // 新增
            addUser(this.userForm).then(res => {
              this.$message.success(res.message || '添加成功')
              this.dialogVisible = false
              this.fetchUserList()
            }).catch(err => {
              this.$message.error(err.message || '添加失败')
            })
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.user-management {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
  position: relative;
}

/* 圆形退出按钮容器 */
.logout-button-container {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 100;
}

/* 圆形退出按钮样式 */
.logout-button {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background-color: #f56c6c;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(245, 108, 108, 0.3);
  transition: all 0.3s ease;
}

.logout-button:hover {
  background-color: #f78989;
  box-shadow: 0 6px 16px rgba(245, 108, 108, 0.4);
}

.logout-button:active {
  transform: scale(0.95);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.page-header .left-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.search-bar {
  margin-bottom: 20px;
}

.table-container {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.dialog-footer {
  text-align: right;
}
</style>
